import {
  BoundingSphere,
  Cartesian3,
  CesiumTerrainProvider,
  EllipsoidalOccluder,
  Math as CesiumMath,
  OrientedBoundingBox,
  QuantizedMeshTerrainData,
  Rectangle,
  Request,
  TerrainData,
  TerrainProvider,
} from "cesium";
import { Egm96Grid } from "../utils/Egm96Grid";

// Cesium internals: every vertex height is interpolated between the minimum and
// maximum height, so shifting both moves the whole tile. The tests pin these names.
type QuantizedMeshTerrainDataInternals = {
  _minimumHeight: number;
  _maximumHeight: number;
  _boundingSphere: BoundingSphere;
  _orientedBoundingBox: OrientedBoundingBox;
  _horizonOcclusionPoint: Cartesian3;
};

/**
 * Serves terrain whose heights are above the EGM96 geoid as heights above the
 * WGS84 ellipsoid, by raising each tile by the geoid undulation at its centre.
 */
export default class GeoidCorrectedTerrainProvider implements TerrainProvider {
  constructor(
    private readonly _inner: CesiumTerrainProvider,
    private readonly _grid: Egm96Grid,
  ) {}

  get errorEvent() {
    return this._inner.errorEvent;
  }

  get credit() {
    return this._inner.credit;
  }

  get tilingScheme() {
    return this._inner.tilingScheme;
  }

  get hasWaterMask() {
    return this._inner.hasWaterMask;
  }

  get hasVertexNormals() {
    return this._inner.hasVertexNormals;
  }

  get availability() {
    return this._inner.availability;
  }

  getLevelMaximumGeometricError(level: number) {
    return this._inner.getLevelMaximumGeometricError(level);
  }

  getTileDataAvailable(x: number, y: number, level: number) {
    return this._inner.getTileDataAvailable(x, y, level);
  }

  loadTileDataAvailability(x: number, y: number, level: number) {
    return this._inner.loadTileDataAvailability(x, y, level);
  }

  requestTileGeometry(
    x: number,
    y: number,
    level: number,
    request?: Request,
  ): Promise<TerrainData> | undefined {
    const tileLoad = this._inner.requestTileGeometry(x, y, level, request);
    if (!tileLoad) return undefined;

    return tileLoad.then((terrainData) =>
      this.raiseToEllipsoid(terrainData, x, y, level),
    );
  }

  private raiseToEllipsoid(
    terrainData: TerrainData,
    x: number,
    y: number,
    level: number,
  ) {
    if (!(terrainData instanceof QuantizedMeshTerrainData)) return terrainData;

    const rectangle = this.tilingScheme.tileXYToRectangle(x, y, level);
    const tileCentre = Rectangle.center(rectangle);
    const undulation = this._grid.undulationAt(
      CesiumMath.toDegrees(tileCentre.longitude),
      CesiumMath.toDegrees(tileCentre.latitude),
    );
    if (undulation === 0) return terrainData;

    const ellipsoid = this.tilingScheme.ellipsoid;
    const internals =
      terrainData as unknown as QuantizedMeshTerrainDataInternals;

    internals._minimumHeight += undulation;
    internals._maximumHeight += undulation;

    const orientedBoundingBox = OrientedBoundingBox.fromRectangle(
      rectangle,
      internals._minimumHeight,
      internals._maximumHeight,
      ellipsoid,
    );
    internals._orientedBoundingBox = orientedBoundingBox;

    const sphereCentre = internals._boundingSphere.center;
    const raisedSphereCentre = Cartesian3.add(
      sphereCentre,
      Cartesian3.multiplyByScalar(
        ellipsoid.geodeticSurfaceNormal(sphereCentre),
        undulation,
        new Cartesian3(),
      ),
      new Cartesian3(),
    );
    internals._boundingSphere = new BoundingSphere(
      raisedSphereCentre,
      internals._boundingSphere.radius,
    );

    internals._horizonOcclusionPoint = new EllipsoidalOccluder(
      ellipsoid,
    ).computeHorizonCullingPointPossiblyUnderEllipsoid(
      raisedSphereCentre,
      OrientedBoundingBox.computeCorners(orientedBoundingBox),
      internals._minimumHeight,
    );

    return terrainData;
  }
}
